import React, { useCallback, useEffect, useState } from "react";
import { Box, Card, CardContent, CardHeader, Link } from "@mui/material";
import { find, map, mergeRight, propEq } from "ramda";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Grid from "@mui/material/Grid";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import LabeledReadOnly from "../../components/LabeledReadOnly";
import YesNoCard from "../../components/YesNoCard";
import makeStyles from "@mui/styles/makeStyles";
import ReadOnlyField from "../../components/ReadOnlyField";
import { useOrganizations } from "../../user-admin/userServices";
import EditProjectDetailsDialog from "../edit/EditProjectDetailsDialog";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const ProjectDetailsCard = ({ project, loading, onUpdate }) => {
  const classes = useStyles();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const handleEditClick = useCallback(() => {
    console.log("Handling Edit click...");
    setEditDialogOpen(true);
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  const handleEditApply = useCallback(
    async data => {
      //call parent callback to save updated data.
      setEditDialogOpen(false);
      console.log(data);
      const update = mergeRight(project, data);
      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  const [organizations, setOrganizations] = useState([]);
  const organizationsResponse = useOrganizations();
  useEffect(() => {
    if (
      organizationsResponse.isSuccess &&
      notBlank(organizationsResponse.data)
    ) {
      setOrganizations(organizationsResponse.data);
    }
  }, [organizationsResponse.isSuccess, organizationsResponse.data]);

  const findOrganizationName = useCallback(
    orgId => {
      if (isBlank(orgId)) {
        return null;
      }

      const organization = find(propEq("organizationId", orgId), organizations);
      return notBlank(organization) ? organization.organizationName : null;
    },
    [organizations],
  );

  const mapDefaultLanguage = useCallback(() => {
    switch (project.defaultLanguage) {
      case "ar":
        return "Arabic";
      case "en":
        return "English";
      case "es":
        return "Spanish";
      case null:
      default:
        return null;
    }
  }, [project.defaultLanguage]);

  const mapDefaultPubDateFormat = useCallback(() => {
    switch (project.defaultPublicationDateFormat) {
      case "year":
        return "Publication Year";
      case "date":
        return "Publication Date";
      case null:
      default:
        return null;
    }
  }, [project.defaultPublicationDateFormat]);

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Details"
        action={
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Tooltip title="Edit Details">
              <IconButton onClick={handleEditClick} size="large">
                <EditIcon color="primary" fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(project) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Project Details.
          </Box>
        )}
        {notBlank(project) && (
          <Grid container spacing={3}>
            <Grid item sm={5}>
              <Grid container spacing={3}>
                <Grid item sm={8}>
                  <LabeledReadOnly label="Project Name">
                    <ReadOnlyField>{project.projectName}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="Auto Approve">
                    <YesNoCard value={project.autoApprove} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={8}>
                  <LabeledReadOnly label="Project Key">
                    <ReadOnlyField>{project.projectKey}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="Force Inactive">
                    <YesNoCard value={project.forceInactive} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={12}>
                  <LabeledReadOnly label="Terms URI">
                    <ReadOnlyField
                      component={Link}
                      href={project.termsUri}
                      target="_blank"
                    >
                      {project.termsUri}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={12}>
                  <LabeledReadOnly label="Redirect URI">
                    <ReadOnlyField
                      component={Link}
                      href={project.redirectUri}
                      target="_blank"
                    >
                      {project.redirectUri}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>
              </Grid>
            </Grid>

            <Grid item sm={4}>
              <Grid container spacing={3}>
                <Grid item sm={6}>
                  <LabeledReadOnly label="Collect Purchase Link">
                    <YesNoCard value={project.collectPurchaseLink} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Use Default Categories">
                    <YesNoCard value={project.useDefaultCategories} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Start Date">
                    <ReadOnlyField>
                      {notBlank(project.startDate)
                        ? new Date(project.startDate).toLocaleDateString()
                        : null}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="End Date">
                    <ReadOnlyField>
                      {notBlank(project.dateExpires)
                        ? new Date(project.dateExpires).toLocaleDateString()
                        : null}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={8}>
                  <LabeledReadOnly label="Default Library">
                    {findOrganizationName(project.defaultLibraryId)}
                  </LabeledReadOnly>
                </Grid>
              </Grid>
            </Grid>

            <Grid item sm={3}>
              <Grid container spacing={3}>
                <Grid item>
                  <LabeledReadOnly label="Hide Cover Image Upload">
                    <YesNoCard value={project.hideCoverImageUpload} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item>
                  <LabeledReadOnly label="Default Project Language">
                    <ReadOnlyField>{mapDefaultLanguage()}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item>
                  <LabeledReadOnly label="Default Pub. Date Format">
                    <ReadOnlyField>{mapDefaultPubDateFormat()}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item>
                  <LabeledReadOnly label="Audiences">
                    {notBlank(project.audiences) ? (
                      map(
                        audience => (
                          <Typography key={audience.name}>
                            {audience.display}
                          </Typography>
                        ),
                        project.audiences,
                      )
                    ) : (
                      <Typography>--</Typography>
                    )}
                  </LabeledReadOnly>
                </Grid>

                <Grid item>
                  <LabeledReadOnly label="Send Rejection Email">
                    <YesNoCard value={project.sendRejectionEmail} />
                  </LabeledReadOnly>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </CardContent>
      {editDialogOpen && (
        <EditProjectDetailsDialog
          open={editDialogOpen}
          details={project}
          onCancel={handleEditCancel}
          onApply={handleEditApply}
        />
      )}
    </Card>
  );
};

export default ProjectDetailsCard;
